import React from "react";
import { useNavigate } from "react-router-dom";
import { Check, Minus } from "lucide-react";
import { cn } from "lib/utils";
import PeerSimilarityBadge from "./PeerSimilarityBadge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "components/ui/table";
import { Button } from "components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "components/ui/avatar";

const PeerSuggestionPanel = ({
  baseEntity,
  suggestedPeers,
  selectedPeers,
  onTogglePeer,
  onToggleSelectAll,
  isLoading,
  filters
}) => {
  const navigate = useNavigate();

  const isAllSelected = suggestedPeers.length > 0 && selectedPeers.length === suggestedPeers.length;
  const isIndeterminate = selectedPeers.length > 0 && selectedPeers.length < suggestedPeers.length;

  const handleSelectAllChange = (e) => {
    e?.stopPropagation?.();
    if (onToggleSelectAll) {
      onToggleSelectAll(!isAllSelected);
    }
  };

  const handleStartBenchmarking = () => {
    if (!baseEntity) return;
    const entitiesToBenchmark = [baseEntity, ...selectedPeers];
    navigate("/explore/analysis", {
      state: {
        entityType: baseEntity.type || "institution",
        selectedEntities: entitiesToBenchmark,
        filters: filters
      }
    });
  };

  return (
    <Card className="w-full shadow-sm border-slate-200">
      <CardHeader className="flex flex-row items-center justify-between p-6 pb-5">
        <div className="space-y-1">
          <CardTitle className="text-xl font-bold flex items-center gap-2">
            Suggested Peers for <span className="text-explore">{baseEntity.name}</span>
          </CardTitle>
          <CardDescription>
            We found {suggestedPeers.length} similar entities based on research topics and performance.
          </CardDescription>
        </div>
        <div className="flex items-center">
          <div className="text-sm font-medium bg-slate-50 px-3 py-1.5 rounded-md border border-slate-200">
            <span className={selectedPeers.length > 0 ? "text-explore font-bold" : "text-slate-400"}>
              {selectedPeers.length}
            </span>
            <span className="text-slate-500"> / {suggestedPeers.length} selected</span>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="p-0 border-t border-slate-100">
        <div className="w-full overflow-x-auto custom-scrollbar">
          <Table>
            <TableHeader className="sticky top-0 bg-white z-10 outline outline-1 outline-slate-100">
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-[50px] text-center font-semibold pl-6">
                  <button
                    type="button"
                    onClick={handleSelectAllChange}
                    disabled={isLoading || suggestedPeers.length === 0}
                    className="mx-auto flex h-[18px] w-[18px] items-center justify-center rounded-[4px] transition-colors focus:outline-none disabled:opacity-50 cursor-pointer"
                    style={{ padding: 0 }}
                    aria-label={isAllSelected ? "Deselect all peers" : "Select all peers"}
                    title={isAllSelected ? "Deselect all" : "Select all"}
                  >
                    <div
                      className={cn(
                        "flex h-full w-full items-center justify-center rounded-[4px] border transition-colors",
                        isAllSelected || isIndeterminate
                          ? "bg-explore border-explore text-white"
                          : "border-slate-300 bg-white hover:border-slate-400"
                      )}
                    >
                      {isAllSelected && <Check size={12} strokeWidth={4} />}
                      {!isAllSelected && isIndeterminate && <Minus size={12} strokeWidth={4} />}
                    </div>
                  </button>
                </TableHead>
                <TableHead className="font-semibold">Institution</TableHead>
                <TableHead className="text-right w-[140px] font-semibold">H-Index</TableHead>
                <TableHead className="text-right w-[140px] font-semibold">Citations</TableHead>
                <TableHead className="text-right w-[140px] font-semibold">Pubs</TableHead>
                <TableHead className="text-center w-[160px] font-semibold pr-6">Similarity</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-32 text-center text-slate-500">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <div className="w-6 h-6 border-2 border-explore border-t-transparent rounded-full animate-spin" />
                      <span className="text-sm">Finding best matches...</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : suggestedPeers.map(peer => {
                const isSelected = selectedPeers.some(p => p.id === peer.id);
                return (
                  <TableRow 
                    key={peer.id} 
                    className={cn(
                      "cursor-pointer transition-colors", 
                      isSelected ? "bg-explore/5 hover:bg-explore/10" : "hover:bg-slate-50/50"
                    )}
                    onClick={() => onTogglePeer(peer)}
                  >
                    <TableCell className="text-center align-middle pl-6">
                      <div
                        className={cn(
                          "mx-auto flex h-[18px] w-[18px] items-center justify-center rounded-[4px] border transition-colors",
                          isSelected
                            ? "bg-explore border-explore text-white"
                            : "border-slate-300 bg-white hover:border-slate-400"
                        )}
                      >
                        {isSelected && <Check size={12} strokeWidth={4} />}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="w-9 h-9 rounded-lg bg-white border border-border/50 p-0.5 shrink-0">
                          <AvatarImage
                            src={(peer.id ? `https://media.academic.online/institutions_images/${peer.id}/seal.png` : null) || peer.logo_url}
                            alt={peer.name}
                            className="object-contain"
                          />
                          <AvatarFallback className="bg-explore/5 text-explore font-bold rounded-md text-xs">
                            {peer.name?.substring(0, 2).toUpperCase() || "UN"}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col min-w-0">
                          <span className="text-sm font-semibold text-slate-900 truncate">{peer.name}</span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-medium text-slate-700">
                      {(peer.h_index || 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right font-medium text-slate-700">
                      {(peer.total_citations || 0) > 1000000 
                        ? ((peer.total_citations || 0) / 1000000).toFixed(1) + "M"
                        : (peer.total_citations || 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right font-medium text-slate-700">
                      {(peer.total_pubs || 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-center align-middle pr-6">
                      <div className="flex justify-center">
                        <PeerSimilarityBadge similarity={peer.peer_score !== undefined ? Math.round(peer.peer_score * 100) : (peer.similarity || 0)} keywords={peer.keywords || []} />
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
      <div className="px-6 py-4 border-t border-slate-100 flex justify-end">
        <Button 
          onClick={handleStartBenchmarking} 
          disabled={selectedPeers.length === 0} 
          className="bg-explore text-white hover:bg-explore/90"
        >
          Start Benchmarking with Peers
        </Button>
      </div>
    </Card>
  );
};

export default PeerSuggestionPanel;
